import { createContext, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

interface TableProps extends React.PropsWithChildren, React.ComponentPropsWithoutRef<'table'> {
    dark?: boolean;
    bordered?: boolean;
    hover?: boolean;
    striped?: boolean;
    sm?: boolean;
}

interface TheadProps extends React.PropsWithChildren, React.ComponentPropsWithoutRef<'thead'> {
    variant?: 'default' | 'light' | 'dark';
}

type TbodyProps = React.PropsWithChildren<React.ComponentPropsWithoutRef<'tbody'>>;
type TrProps = React.PropsWithChildren & React.ComponentPropsWithoutRef<'tr'>;
type ThProps = React.PropsWithChildren & React.ComponentPropsWithoutRef<'th'>;
type TdProps = React.PropsWithChildren & React.ComponentPropsWithoutRef<'td'>;

type TableCompoundComponent = React.FC<TableProps> & {
    Thead: React.FC<TheadProps>;
    Tbody: React.FC<TbodyProps>;
    Tr: React.FC<TrProps>;
    Th: React.FC<ThProps>;
    Td: React.FC<TdProps>;
};

const tableContext = createContext<{
    dark: TableProps['dark'];
    bordered: TableProps['bordered'];
    hover: TableProps['hover'];
    striped: TableProps['striped'];
    sm: TableProps['sm'];
}>({
    dark: false,
    bordered: false,
    hover: false,
    striped: false,
    sm: false,
});

const theadContext = createContext<{
    variant: TheadProps['variant'];
}>({
    variant: 'default',
});

const TableRoot: React.FC<TableProps> = ({ className, dark, bordered, hover, striped, sm, ...props }: TableProps) => (
    <tableContext.Provider
        value={{
            dark: dark,
            bordered: bordered,
            hover: hover,
            striped: striped,
            sm: sm,
        }}
    >
        <table className={twMerge(['w-full text-left', dark && 'bg-dark text-white dark:bg-black/30', className])} {...props}>
            {props.children}
        </table>
    </tableContext.Provider>
);

const TableThead: React.FC<TheadProps> = ({ className, ...props }) => (
    <theadContext.Provider value={{ variant: props.variant }}>
        <thead className={twMerge([props.variant === 'light' && 'bg-neutro-100 dark:bg-darkmode-700', props.variant === 'dark' && 'bg-dark text-white dark:bg-black/30', className])} {...props}>
            {props.children}
        </thead>
    </theadContext.Provider>
);

const TableTbody: React.FC<TbodyProps> = ({ className, ...props }) => <tbody className={className}>{props.children}</tbody>;

const TableTr: React.FC<TrProps> = ({ className, ...props }) => {
    const table = useContext(tableContext);
    return (
        <tr
            className={twMerge([
                table.hover && '[&:hover_td]:bg-roxo-50 [&:hover_td]:dark:bg-darkmode-500/60',
                table.striped && '[&:nth-of-type(odd)_td]:bg-neutro-50 [&:nth-of-type(odd)_td]:dark:bg-darkmode-700/60',
                className,
            ])}
            {...props}
        >
            {props.children}
        </tr>
    );
};

const TableTh: React.FC<ThProps> = ({ className, ...props }) => {
    const table = useContext(tableContext);
    const thead = useContext(theadContext);
    return (
        <th
            className={twMerge([
                // Rodada 2 (28/10): cabeçalho nítido, em caixa alta pequena sobre Neutro 50,
                // com filete Neutro 300 embaixo.
                'px-5 py-3 border-b border-neutro-300 bg-neutro-50 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutro-700 dark:bg-darkmode-700 dark:text-slate-300 dark:border-white/15',
                thead.variant === 'light' && 'text-neutro-800',
                thead.variant === 'dark' && 'border-b-0 bg-transparent text-white dark:bg-transparent',
                table.dark && 'border-slate-600 bg-transparent text-white dark:border-darkmode-300 dark:bg-transparent',
                table.bordered && 'border-l border-r border-t',
                table.sm && 'px-4 py-2',
                className,
            ])}
            {...props}
        >
            {props.children}
        </th>
    );
};

const TableTd: React.FC<TdProps> = ({ className, ...props }) => {
    const table = useContext(tableContext);
    return (
        <td
            className={twMerge([
                'px-5 py-3 border-b border-neutro-200 dark:border-white/10',
                table.dark && 'border-slate-600 dark:border-darkmode-300',
                table.bordered && 'border-l border-r border-t',
                table.sm && 'px-4 py-2',
                className,
            ])}
            {...props}
        >
            {props.children}
        </td>
    );
};

export type TableComponent = TableCompoundComponent;

const Table = TableRoot as TableCompoundComponent;
Table.Thead = TableThead;
Table.Tbody = TableTbody;
Table.Tr = TableTr;
Table.Th = TableTh;
Table.Td = TableTd;

export default Table;
